import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, DollarSign, Check, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { createBatchPayment, getListProfits } from "@/api/Admin/Profit";
import { toast } from "react-toastify";
import Spinner from "@/components/Spinner";

type PackageTerm = "short" | "medium" | "long";

type PackagePayment = {
  id: string;
  packageName: string;
  packageCode: string;
  packageTerm: PackageTerm;
  paymentPeriod: string;
  interestAmount: number;
  taxAmount: number;
  netAmount: number;
  dueDate: string;
};

type InvestorPayment = {
  investorId: string;
  investorName: string;
  investorCode: string;
  paymentPeriod: string;
  totalInterest: number;
  totalTax: number;
  totalNet: number;
  packages: PackagePayment[];
};


const CreateBatch: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [batchName, setBatchName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set());
  const [pendingPayments, setPendingPayments] = useState<InvestorPayment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getListProfits({ page: 1, limit: 1000, status: "pending", batch: true, search: searchTerm });
        if (res?.data?.data) {
          const mappedData: InvestorPayment[] = res.data.data.map((item: any) => {
            const packages: PackagePayment[] = item.items.map((pkg: any) => ({
              id: pkg.id.toString(),
              packageName: pkg.package_name,
              packageCode: "N/A",
              packageTerm: "long", // Default or map if available
              paymentPeriod: pkg.period,
              interestAmount: Number(pkg.gross_amount),
              taxAmount: Number(pkg.tax_fee),
              netAmount: Number(pkg.net_amount),
              dueDate: pkg.due_date,
            }));

            const totalInterest = packages.reduce((sum, p) => sum + p.interestAmount, 0);
            const totalTax = packages.reduce((sum, p) => sum + p.taxAmount, 0);
            const totalNet = packages.reduce((sum, p) => sum + p.netAmount, 0);

            return {
              investorId: item.user_id.toString(),
              investorName: item.user_info.fullname,
              investorCode: item.user_info.cccd_number || `BEQ${item.user_id}`,
              paymentPeriod: item.items.length > 0 ? item.items[0].period : "",
              totalInterest,
              totalTax,
              totalNet,
              packages,
            };
          });
          setPendingPayments(mappedData);
        }
      } catch (error) {
        console.error(error);
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t, searchTerm]);

  // Get current month/year for display
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("interestPayment.title"), path: "/admin/interest-payments" },
    { label: t("createBatch.title") },
  ];

  // search
  const filteredData = useMemo(() => {
    return pendingPayments.filter((investor) => {
      const matchesSearch =
        investor.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        investor.investorCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        investor.packages.some(pkg =>
          pkg.packageCode.toLowerCase().includes(searchTerm.toLowerCase())
        );
      return matchesSearch;
    });
  }, [pendingPayments, searchTerm]);

  // Calculate totals
  const totalPendingCount = useMemo(() => {
    return pendingPayments.reduce((sum, inv) => sum + inv.packages.length, 0);
  }, [pendingPayments]);

  const selectedStats = useMemo(() => {
    let count = 0;
    let totalNet = 0;

    pendingPayments.forEach(investor => {
      investor.packages.forEach(pkg => {
        if (selectedPackages.has(pkg.id)) {
          count++;
          totalNet += pkg.netAmount;
        }
      });
    });

    return { count, totalNet };
  }, [pendingPayments, selectedPackages]);

  // Check if all packages of an investor are selected
  const isInvestorFullySelected = (investor: InvestorPayment) => {
    return investor.packages.every(pkg => selectedPackages.has(pkg.id));
  };

  // Check if some packages of an investor are selected
  const isInvestorPartiallySelected = (investor: InvestorPayment) => {
    const selectedCount = investor.packages.filter(pkg => selectedPackages.has(pkg.id)).length;
    return selectedCount > 0 && selectedCount < investor.packages.length;
  };

  // Toggle investor selection (all packages)
  const toggleInvestorSelection = (investor: InvestorPayment) => {
    const newSelected = new Set(selectedPackages);
    if (isInvestorFullySelected(investor)) {
      investor.packages.forEach(pkg => newSelected.delete(pkg.id));
    } else {
      investor.packages.forEach(pkg => newSelected.add(pkg.id));
    }
    setSelectedPackages(newSelected);
  };

  // Toggle single package selection
  const togglePackageSelection = (packageId: string) => {
    const newSelected = new Set(selectedPackages);
    if (newSelected.has(packageId)) {
      newSelected.delete(packageId);
    } else {
      newSelected.add(packageId);
    }
    setSelectedPackages(newSelected);
  };

  // Select all
  const selectAll = () => {
    const allPackageIds = new Set<string>();
    filteredData.forEach(investor => {
      investor.packages.forEach(pkg => {
        allPackageIds.add(pkg.id);
      });
    });
    setSelectedPackages(allPackageIds);
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedPackages(new Set());
  };

  const getPackageTermBadge = (term: PackageTerm) => {
    const badges = {
      short: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      long: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    };
    const labels = {
      short: "Ngắn hạn",
      medium: "Trung hạn",
      long: "Dài hạn",
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${badges[term]}`}
      >
        {labels[term]}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + " đ";
  };

  const handleCancel = () => {
    navigate("/admin/interest-payments");
  };

  const handleCreateBatch = () => {
    // call api here
    createBatchPayment(batchName, Array.from(selectedPackages));
    navigate("/admin/batch-history");
  };

  return (
    <div className="dark:bg-dark-bg pb-24">
      <Helmet>
        <title>{t("createBatch.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("createBatch.title")}
        description={`${t("createBatch.month")} ${currentMonth}/${currentYear}`}
        breadcrumbs={breadcrumbs}
      />

      {/* Batch Name Input */}
      <div className="bg-white dark:bg-gray-800 rounded-md p-6 border border-gray-200 dark:border-gray-700 mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("createBatch.batchName")} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={batchName}
          onChange={(e) => setBatchName(e.target.value)}
          placeholder={t("createBatch.batchNamePlaceholder")}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("createBatch.totalPending")}
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {totalPendingCount}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-md">
              <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("createBatch.selected")}
              </p>
              <p className="text-3xl font-bold text-primary dark:text-primary mt-1">
                {selectedStats.count}
              </p>
            </div>
            <div className="p-3 bg-primary/10 dark:bg-primary/20 rounded-md">
              <Check className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("createBatch.totalPayment")}
              </p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-1">
                {formatCurrency(selectedStats.totalNet)}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-md">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t("createBatch.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={selectAll}
            className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md font-medium transition-colors text-sm"
          >
            {t("createBatch.selectAll")}
          </button>
          <button
            onClick={deselectAll}
            className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md font-medium transition-colors text-sm"
          >
            {t("createBatch.deselectAll")}
          </button>
        </div>
      </div>

      {/* Investor List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner size={40} />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            {t("createBatch.noData")}
          </div>
        ) : (
          filteredData.map((investor) => (
            <div
              key={investor.investorId}
              className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Investor Header */}
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-start gap-3">
                  <div className="pt-1">
                    <input
                      type="checkbox"
                      checked={isInvestorFullySelected(investor)}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = isInvestorPartiallySelected(investor);
                        }
                      }}
                      onChange={() => toggleInvestorSelection(investor)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-base font-semibold text-gray-900 dark:text-white">
                        {investor.investorName}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs font-medium">
                        {investor.investorCode}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("createBatch.numPackages")}: <span className="text-primary font-medium">{investor.packages.length} {t("createBatch.packages")}</span>
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("createBatch.totalInterest")}: <span className="text-gray-900 dark:text-white font-medium">{formatCurrency(investor.totalInterest)}</span>
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("createBatch.totalTax")}: <span className="text-red-500 font-medium">{formatCurrency(investor.totalTax)}</span>
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("createBatch.totalNet")}: <span className="text-green-600 dark:text-green-400 font-semibold">{formatCurrency(investor.totalNet)}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Package List */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {investor.packages.map((pkg) => (
                      <tr
                        key={pkg.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="py-3 pl-12 pr-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectedPackages.has(pkg.id)}
                            onChange={() => togglePackageSelection(pkg.id)}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        {/* Package Name & Code */}
                        <td className="py-3 px-3 w-64">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {pkg.packageName}
                          </p>
                          {/* <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("createBatch.code")}: {pkg.packageCode}
                          </p> */}
                        </td>
                        {/* Payment Period & Due Date */}
                        <td className="py-3 px-3 text-center w-64">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("createBatch.dueDate")}:<br />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {pkg.dueDate}
                            </p>
                          </p>
                        </td>
                        {/* Interest Amount */}
                        <td className="py-3 px-3 text-right w-64">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t("createBatch.interest")}</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(pkg.interestAmount)}
                          </p>
                        </td>
                        {/* Tax Amount */}
                        <td className="py-3 px-3 text-right w-64">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t("createBatch.tax")}</p>
                          <p className="text-sm font-medium text-red-500">
                            {formatCurrency(pkg.taxAmount)}
                          </p>
                        </td>
                        {/* Net Amount */}
                        <td className="py-3 px-3 pr-4 text-right w-64">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t("createBatch.netReceived")}</p>
                          <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(pkg.netAmount)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Button Footer */}
      <div className="fixed bottom-0 right-0 left-0 md:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 z-40">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("createBatch.selectedCount", { count: selectedStats.count })}
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {t("createBatch.totalAmount")}: <span className="text-green-600 dark:text-green-400">{formatCurrency(selectedStats.totalNet)}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md font-medium transition-colors text-sm"
            >
              <X className="w-4 h-4" />
              {t("createBatch.cancel")}
            </button>
            <button
              onClick={handleCreateBatch}
              disabled={!batchName.trim() || selectedStats.count === 0}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors text-sm"
            >
              {t("createBatch.createButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBatch;
